<template>
  <div class="payment-method-tabs">
    <!-- Payment Method Selection (buttons) -->
    <div class="payment-buttons d-flex flex-wrap mb-2">
      <v-btn
        v-for="method in visiblePaymentMethods"
        :key="method.value"
        small
        depressed
        :class="[
          'payment-tab',
          {
            'payment-tab--active': mapValueToPane(method.value) === selectedTab,
          },
        ]"
        @click="selectMethod(mapValueToPane(method.value))"
      >
        <span class="payment-label">{{ $t(method.name) }}</span>
      </v-btn>
    </div>

    <!-- Payment Method Content -->
    <div class="payment-content">
      <!-- Unified renderer for simple methods: cash, transfer, credit, cod -->
      <template v-if="isSimpleMethod">
        <v-card flat>
          <v-card-text class="pa-0" v-if="!isQRActive || selectedTab !== 'qr'">
            <div class="payment-input-section">
              <NumericKeyboard
                v-if="!isLoading && !isProcessing"
                :total-price="totalAmount"
                :value="simpleAmountValue"
                :type="storeType"
                :currencyData="keyboardCurrencyData"
                :show-note="false"
                :debug="debug"
                @input="updateSimpleAmount"
                @submit="handleSimplePayment"
                @customer="onCustomer"
                @openNoted="onOpenNoted"
                @exchangeRate="onExchangeRate"
              />
              <!-- Loading overlay during payment processing -->
              <div v-if="isProcessing" class="payment-processing-overlay">
                <v-progress-circular indeterminate color="primary" size="64" />
                <div class="text-subtitle-1 mt-4">
                  {{ $t("processing_payment") || "Processing payment..." }}
                </div>
              </div>
            </div>
          </v-card-text>
          <template v-if="isQRActive && selectedTab === 'qr'">
            <!-- Skeleton while loading -->
            <div v-if="qrLoading" class="text-center pa-5">
              <div class="skeleton-qr-container mb-4">
                <v-skeleton-loader
                  type="image"
                  width="200"
                  height="200"
                  class="mx-auto"
                ></v-skeleton-loader>
              </div>
              <div class="skeleton-price-section">
                <v-skeleton-loader
                  type="text"
                  width="150"
                  class="mx-auto mb-2"
                ></v-skeleton-loader>
                <v-skeleton-loader
                  type="text"
                  width="200"
                  class="mx-auto mb-3"
                ></v-skeleton-loader>
                <v-skeleton-loader
                  type="text"
                  width="180"
                  class="mx-auto mb-3"
                ></v-skeleton-loader>
              </div>
            </div>

            <!-- QR Display -->
            <div v-else-if="qrCodeData" class="text-center">
              <div class="text-h6 mb-3">{{ $t("scan_qr_to_pay") }}</div>
              <div class="qr-container mb-4">
                <div class="qr-wrapper">
                  <img
                    :src="qrCodeData.qrCode"
                    alt="QR Code"
                    class="qr-image"
                  />
                  <div class="qr-image-wrapper"></div>
                  <div class="qr-overlay">
                    <img
                      src="/images/lao_qr.png"
                      alt="Lao QR"
                      class="lao-qr-overlay"
                      @error="handleLaoQRError"
                    />
                  </div>
                </div>
              </div>
              <div class="price-amount-section">
                <div class="text-h5 font-weight-bold primary--text mb-2">
                  {{ qrCodeData.amount | NumberFormat }}
                </div>
                <div class="text-body-2 grey--text mb-3">
                  {{ qrCodeData.terminal_label }}
                </div>
              </div>
              <div class="d-flex justify-center gap-2 mb-4">
                <v-btn
                  small
                  outlined
                  color="primary"
                  @click="openManualPaymentDialog"
                >
                  <v-icon left small>fal fa-edit</v-icon
                  >{{ $t("manual_confirmation") }}
                </v-btn>
                <v-btn
                  small
                  outlined
                  color="error"
                  @click="switchToCashPayment"
                >
                  <v-icon left small>fal fa-money-bill</v-icon
                  >{{ $t("switch_to_cash") }}
                </v-btn>
              </div>
            </div>

            <!-- No QR yet (auto-generating) -->
            <div v-else class="text-center pa-5">
              <v-progress-circular indeterminate color="primary" />
              <div class="text-caption grey--text mt-2">
                {{ $t("preparing_qr") }}
              </div>
            </div>
          </template>
        </v-card>
      </template>

      <!-- Cash with Transfer Payment -->
      <template v-if="selectedTab === 'cash_transfer'">
        <v-card flat>
          <v-card-text class="px-0">
            <v-row>
              <v-col cols="6">
                <div class="text-subtitle-2 mb-2">{{ $t("cash_amount") }}</div>
                <v-card
                  color="grey lighten-4"
                  :flat="activeInput !== 'cash'"
                  class="mb-2"
                  :outlined="activeInput !== 'cash'"
                  :class="activeInput === 'cash' ? 'border-primary' : ''"
                  @click="setActiveInput('cash')"
                >
                  <v-card-text
                    class="d-flex justify-space-between align-center py-2 px-2"
                  >
                    <v-btn icon @click="clearCashAmount" x-small>
                      <v-icon x-small>fal fa-times-circle</v-icon>
                    </v-btn>
                    <div class="font-weight-bold text-h6">
                      {{ formattedCashAmount }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="6">
                <div class="text-subtitle-2 mb-2">
                  {{ $t("transfer_amount") }}
                </div>
                <v-card
                  color="grey lighten-4"
                  :flat="activeInput !== 'transfer'"
                  class="mb-2"
                  :outlined="activeInput !== 'transfer'"
                  :class="activeInput === 'transfer' ? 'border-primary' : ''"
                  @click="setActiveInput('transfer')"
                >
                  <v-card-text
                    class="d-flex justify-space-between align-center py-2 px-2"
                  >
                    <v-btn icon @click="clearTransferAmount" x-small>
                      <v-icon x-small>fal fa-times-circle</v-icon>
                    </v-btn>
                    <div class="font-weight-bold text-h6">
                      {{ formattedTransferAmount }}
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>

            <!-- Warning when cash + transfer is less than total -->
            <div
              v-if="showInsufficientPaymentWarning"
              class="mt-2 mb-3 text-center"
            >
              <v-chip
                small
                color="warning"
                text-color="white"
                class="text-caption"
              >
                <v-icon x-small left>fal fa-exclamation-triangle</v-icon>
                {{ $t("cash_transfer_insufficient_warning") }}
              </v-chip>
            </div>

            <div class="active-input-indicator mb-3">
              <v-chip
                :color="activeInput === 'cash' ? 'primary' : 'grey lighten-2'"
                :text-color="activeInput === 'cash' ? 'white' : 'grey darken-2'"
                small
                @click="setActiveInput('cash')"
                class="mr-2"
              >
                {{ $t("cash") }}
              </v-chip>
              <v-chip
                :color="
                  activeInput === 'transfer' ? 'primary' : 'grey lighten-2'
                "
                :text-color="
                  activeInput === 'transfer' ? 'white' : 'grey darken-2'
                "
                small
                @click="setActiveInput('transfer')"
              >
                {{ $t("transfer") }}
              </v-chip>
            </div>

            <NumericKeyboard
              v-if="!isLoading && !isProcessing"
              :show-amount="false"
              :total-price="totalAmount - combinedAmount"
              :value="activeInputValue"
              :type="storeType"
              :currencyData="keyboardCurrencyData"
              :show-note="false"
              :debug="debug"
              @input="updateActiveInput"
              @submit="handleCashTransferPayment"
              @customer="onCustomer"
              @openNoted="onOpenNoted"
              @exchangeRate="onExchangeRate"
            />
            <!-- Loading overlay during payment processing -->
            <div v-if="isProcessing" class="payment-processing-overlay">
              <v-progress-circular indeterminate color="primary" size="64" />
              <div class="text-subtitle-1 mt-4">
                {{ $t("processing_payment") || "Processing payment..." }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </template>
    </div>

    <!-- Manual Payment Confirmation Dialog -->
    <app-custom-dialog
      v-model="showManualPaymentDialog"
      title="manual_payment_confirmation"
      width="450px"
      cancel-text="cancel"
      submit-text="confirm"
      :submit-loading="manualPaymentLoading"
      @submit="confirmManualPayment"
    >
      <div slot="activator">
          <div class="manual-payment-section">
            <v-alert type="info" dense outlined class="mb-4">
              <div class="text-body-2">{{ $t("manual_payment_info") }}</div>
            </v-alert>
            <div class="payment-details mb-4">
              <div class="text-subtitle-1 font-weight-bold mb-2">
                {{ $t("payment_details") }}
              </div>
              <div class="text-body-2 grey--text mb-1">
                {{ $t("bill_number") }}: {{ qrCodeData?.ticket_id }}
              </div>
              <div class="text-body-2 grey--text mb-1">
                {{ $t("amount") }}: {{ qrCodeData?.amount | NumberFormat }}
              </div>
            </div>
            <v-text-field
              v-model="manualPaymentReference"
              :label="$t('payment_reference')"
              :placeholder="$t('enter_payment_reference')"
              :error-messages="manualPaymentErrors"
              outlined
              dense
              required
              clearable
            />
            <div class="text-caption grey--text mt-2">
              {{ $t("manual_payment_reference_hint") }}
            </div>
          </div>
      </div>
    </app-custom-dialog>
  </div>
</template>

<script>
import NumericKeyboard from "@/components/NumericKeyboard";
import { toast } from "@/controllers/alert";
import { myMixin } from "@/mixin/main.js";
import { mainStore } from "@/stores/modules/cart";

export default {
  name: "UnifiedPaymentPad",
  mixins: [myMixin],
  components: { NumericKeyboard },
  props: {
    totalAmount: { type: Number, required: true, default: 0 },
    displayAmount: { type: Number, default: null },
    storeType: { type: String, default: "POS" },
    activeCurrencies: { type: Array, default: () => [] },
    appliedDiscount: { type: Object, default: null },
    debug: { type: Boolean, default: false },
    isProcessing: { type: Boolean, default: false },
  },
  data() {
    return {
      store: mainStore(),
      selectedTab: "cash",
      activeInput: "cash",
      isLoading: false,
      paymentStatus: null,
      // amounts
      cashAmount: "0",
      transferAmount: "0",
      creditAmount: "0",
      // currency
      selectedCurrency: null,
      selectedCurrencyData: null,
      // QR
      qrLoading: false,
      qrCodeData: null,
      manualPaymentLoading: false,
      manualPaymentReference: "",
      manualPaymentErrors: [],
      showManualPaymentDialog: false,
      // Internal state
      isPaymentCompleted: false,
    };
  },
  mounted() {
    this.setDefaultCurrency();

    // Initialize selected tab to first visible method
    if (this.visiblePaymentMethods.length > 0) {
      this.selectedTab = this.mapValueToPane(
        this.visiblePaymentMethods[0].value
      );
    }
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    // Display amount for "Amount Due" - shows full price before discount
    displayAmountForShow() {
      return this.displayAmount !== null && this.displayAmount !== undefined
        ? this.displayAmount
        : this.totalAmount;
    },
    paymentList() {
      // Use filtered payment list based on store settings
      if (this.user && this.user.stores) {
        return this.store.filteredPaymentList(this.user) || [];
      }
      return this.store.paymentList || [];
    },
    isQRActive() {
      return (
        this.user && this.user.stores && this.user.stores.is_active_qr === 1
      );
    },
    isCODActive() {
      return this.user && this.user.stores && this.user.stores.is_cod === 1;
    },
    visiblePaymentMethods() {
      const list = (this.paymentList || []).filter((m) => {
        // filter by store type from cart.js (type: 'pos' | 'restaurant' | 'both')
        if (
          this.storeType === "POS" &&
          m.type &&
          !["pos", "both"].includes(m.type)
        ) {
          return false;
        }
        if (
          (this.storeType === "RESTAURANT" || this.storeType === "BUFFET") &&
          m.type &&
          !["restaurant", "both"].includes(m.type)
        ) {
          return false;
        }

        return true;
      });

      return list;
    },
    formattedCashAmount() {
      return this.$options.filters.NumberFormat(this.cashAmount);
    },
    formattedTransferAmount() {
      return this.$options.filters.NumberFormat(this.transferAmount);
    },
    formattedCreditAmount() {
      return this.$options.filters.NumberFormat(this.creditAmount);
    },
    combinedAmount() {
      const cash = parseFloat(this.cashAmount) || 0;
      const transfer = parseFloat(this.transferAmount) || 0;
      return cash + transfer;
    },
    formattedCombinedAmount() {
      return this.$options.filters.NumberFormat(this.combinedAmount.toString());
    },
    showInsufficientPaymentWarning() {
      // Only show warning for cash_transfer payment method
      if (this.selectedTab !== "cash_transfer") {
        return false;
      }

      // Parse amounts, removing any formatting (commas, spaces)
      const cashStr = String(this.cashAmount || "0")
        .replace(/,/g, "")
        .trim();
      const transferStr = String(this.transferAmount || "0")
        .replace(/,/g, "")
        .trim();

      const cashAmount = parseFloat(cashStr) || 0;
      const transferAmount = parseFloat(transferStr) || 0;

      // Only show warning if both amounts are entered
      if (cashAmount <= 0 || transferAmount <= 0) {
        return false;
      }

      const combinedAmount = cashAmount + transferAmount;

      // Calculate expected amount considering currency conversion
      let expectedAmount = this.totalAmount;
      if (
        this.selectedCurrencyData &&
        this.selectedCurrencyData.store_exchange_rate &&
        this.selectedCurrencyData.code !== "LAK"
      ) {
        const exchangeRate = parseFloat(
          this.selectedCurrencyData.store_exchange_rate
        );
        if (exchangeRate > 0) {
          expectedAmount = this.totalAmount / exchangeRate;
        }
      }

      // Show warning if combined amount is less than expected amount
      return combinedAmount < expectedAmount;
    },
    activeInputValue() {
      return this.activeInput === "cash"
        ? this.cashAmount
        : this.transferAmount;
    },
    isSimpleMethod() {
      return ["cash", "transfer", "qr", "credit", "cod"].includes(
        this.selectedTab
      );
    },
    labelForSimpleMethod() {
      if (this.selectedTab === "transfer") return "transfer_amount";
      if (this.selectedTab === "credit") return "credit_amount";
      return null;
    },
    simpleAmountValue: {
      get() {
        if (this.selectedTab === "cash" || this.selectedTab === "cod")
          return this.cashAmount;
        if (this.selectedTab === "transfer") return this.transferAmount;
        if (this.selectedTab === "credit") return this.creditAmount;
        if (this.selectedTab === "qr") return this.cashAmount;
        return this.cashAmount;
      },
      set(v) {
        this.updateSimpleAmount(v);
      },
    },
    keyboardCurrencyData() {
      if (!this.selectedCurrencyData) return null;
      const buffer =
        this.selectedTab === "cash_transfer"
          ? this.activeInputValue
          : this.simpleAmountValue;

      // Add flag property to currency data
      const flag = "https://devla.s3.ap-southeast-1.amazonaws.com/flags/";
      const flagMap = {
        LAK: flag + "lak.svg",
        USD: flag + "usd.svg",
        THB: flag + "thb.svg",
        CNY: flag + "cny.svg",
        EUR: flag + "eur.svg",
      };
      const currencyFlag =
        flagMap[this.selectedCurrencyData.code] || flag + "ic_currency.svg";

      const data = {
        ...this.selectedCurrencyData,
        input_buffer: buffer,
        flag: currencyFlag,
      };
      return data;
    },
    // Helper to understand the current amount's base currency
    currentAmountInfo() {
      // Check if cart has items with different currencies to determine base currency
      let baseCurrency = "LAK";
      let baseExchangeRate = 1;
      let baseDescription = "Amount is in LAK (default)";

      if (this.store.cart && this.store.cart.length > 0) {
        const firstNonLAKItem = this.store.cart.find(
          (item) => item.currency && item.currency.code !== "LAK"
        );

        if (firstNonLAKItem) {
          baseCurrency = firstNonLAKItem.currency.code;
          baseExchangeRate = firstNonLAKItem.exchange_rate || 1;
          baseDescription = `Amount is in ${baseCurrency} (from cart items)`;
        }
      }

      return {
        amount: this.totalAmount,
        currency: baseCurrency,
        exchangeRate: baseExchangeRate,
        description: baseDescription,
      };
    },
  },
  methods: {
    dlog(label, payload) {
      if (this.debug) {
        try {
          // Debug logging disabled
        } catch (e) {
          // no-op
        }
      }
    },
    selectMethod(pane) {
      this.selectedTab = pane;
    },
    // mapping helpers
    mapValueToPane(value) {
      return value === "cash_with_transfer" ? "cash_transfer" : value;
    },
    updateSimpleAmount(value) {
      if (this.selectedTab === "cash" || this.selectedTab === "cod") {
        this.updateCashAmount(value);
      } else if (this.selectedTab === "transfer") {
        this.updateTransferAmount(value);
      } else if (this.selectedTab === "credit") {
        this.updateCreditAmount(value);
      } else if (this.selectedTab === "qr") {
        this.updateCashAmount(value);
      }
    },
    handleSimplePayment() {
      if (this.selectedTab === "cash") this.handleCashPayment();
      else if (this.selectedTab === "transfer") this.handleTransferPayment();
      else if (this.selectedTab === "credit") this.handleCreditPayment();
      else if (this.selectedTab === "cod") this.handleCODPayment();
      else if (this.selectedTab === "qr") this.handleQRPayment();
    },
    setActiveInput(input) {
      this.activeInput = input;
      this.paymentStatus = null;
      if (this.selectedTab === "cash_transfer") this.validatePayment();
    },
    updateCashAmount(value) {
      this.cashAmount = value;
      this.paymentStatus = null;
    },
    updateTransferAmount(value) {
      this.transferAmount = value;
      this.paymentStatus = null;
    },
    updateCreditAmount(value) {
      this.creditAmount = value;
      this.paymentStatus = null;
    },
    updateActiveInput(value) {
      if (this.activeInput === "cash") this.cashAmount = value;
      else this.transferAmount = value;
      this.paymentStatus = null;
      const currentAmount = parseFloat(value) || 0;
      if (currentAmount >= this.totalAmount) {
        this.activeInput = this.activeInput === "cash" ? "transfer" : "cash";
      }
      if (this.selectedTab === "cash_transfer") this.validatePayment();
    },
    clearCashAmount() {
      this.cashAmount = "0";
      this.paymentStatus = null;
    },
    clearTransferAmount() {
      this.transferAmount = "0";
      this.paymentStatus = null;
    },
    getPaymentAmount() {
      switch (this.selectedTab) {
        case "cash":
          return this.cashAmount;
        case "transfer":
          return this.transferAmount;
        case "credit":
          return this.creditAmount;
        case "qr":
          return this.cashAmount;
        case "cod":
          return this.cashAmount;
        case "cash_transfer":
          return this.combinedAmount.toString();
        default:
          return "0";
      }
    },
    getPaymentDetails() {
      const paymentAmount = parseFloat(this.getPaymentAmount()) || 0;
      const change = Math.max(0, paymentAmount - this.totalAmount);
      const details = {
        method: this.selectedTab,
        totalAmount: this.totalAmount,
        paidAmount: paymentAmount,
        change,
      };
      if (this.selectedTab === "cash_transfer") {
        details.cashAmount = parseFloat(this.cashAmount) || 0;
        details.transferAmount = parseFloat(this.transferAmount) || 0;
      }
      if (this.selectedTab === "qr" && this.qrCodeData) {
        details.qrData = this.qrCodeData;
        details.ticket_id = this.qrCodeData.ticket_id;
      }
      if (this.selectedCurrencyData) {
        details.currency_id = this.selectedCurrencyData.id;
        details.exchange_rate = this.selectedCurrencyData.store_exchange_rate;
        details.currency_code = this.selectedCurrencyData.code;
      }

      return details;
    },
    validatePayment() {
      const amount = parseFloat(this.getPaymentAmount()) || 0;
      const totalDue = Number(this.totalAmount) || 0;
      /** Stamp redeem-only carts can total ₭0 — no cash collection required */
      const isZeroSale = totalDue <= 0.01;

      if (this.selectedTab === "cash_transfer") {
        const cashAmount = parseFloat(this.cashAmount) || 0;
        const transferAmount = parseFloat(this.transferAmount) || 0;
        const combinedAmount = cashAmount + transferAmount;

        let expectedAmount = this.totalAmount;
        const cartCurrency = this.currentAmountInfo.currency;
        if (this.selectedCurrencyData && this.selectedCurrencyData.code !== cartCurrency) {
          let convertedTotal = this.totalAmount;
          if (cartCurrency !== "LAK") {
            const baseRate = parseFloat(this.currentAmountInfo.exchangeRate) || 1;
            convertedTotal = this.totalAmount * baseRate;
          }
          const targetRate = parseFloat(this.selectedCurrencyData.store_exchange_rate) || 1;
          if (targetRate > 0) {
            expectedAmount = convertedTotal / targetRate;
          }
        }

        if (expectedAmount <= 0.01) {
          this.paymentStatus = {
            type: "success",
            message: this.$t("payment_ready"),
          };
          return true;
        }

        if (cashAmount <= 0 && transferAmount <= 0) {
          this.paymentStatus = {
            type: "error",
            message:
              this.$t("at_least_one_payment_amount_required") ||
              "Please enter at least one payment amount",
          };
          return false;
        }
        if (combinedAmount > expectedAmount) {
          this.paymentStatus = {
            type: "error",
            message: this.$t("combined_amount_exceeds_total"),
          };
          return false;
        }
        if (combinedAmount < expectedAmount) {
          this.paymentStatus = {
            type: "warning",
            message: this.$t("insufficient_combined_payment"),
          };
          return false;
        }
        this.paymentStatus = {
          type: "success",
          message: this.$t("payment_ready"),
        };
        return true;
      }
      if (isZeroSale) {
        this.paymentStatus = {
          type: "success",
          message: this.$t("payment_ready"),
        };
        return true;
      }
      if (amount <= 0) {
        this.paymentStatus = {
          type: "error",
          message: this.$t("payment_amount_required"),
        };
        return false;
      }
      this.paymentStatus = {
        type: "success",
        message: this.$t("payment_ready"),
      };
      return true;
    },
    handleCashPayment() {
      this.selectedTab = "cash";
      this.processPayment();
    },
    handleCODPayment() {
      this.selectedTab = "cod";
      this.processPayment();
    },
    handleTransferPayment() {
      this.selectedTab = "transfer";
      this.processPayment();
    },
    handleCreditPayment() {
      this.selectedTab = "credit";
      this.processPayment();
    },
    handleCashTransferPayment() {
      this.selectedTab = "cash_transfer";
      this.processPayment();
    },
    handleQRPayment() {
      const totalDue = Number(this.totalAmount) || 0;
      if (totalDue <= 0.01) {
        this.processPayment();
        return;
      }
      if (this.isQRActive && !this.qrCodeData) {
        // QR is active but no QR data yet, generate QR first
        this.qrLoading = true;
        setTimeout(() => {
          this.emitQRSubmit();
        }, 100);
      } else {
        // Either QR is not active (behave like cash) or we have QR data
        this.processPayment();
      }
    },
    onCustomer() {
      this.$emit("customer");
    },
    onOpenNoted() {
      this.$emit("openNoted");
    },
    onExchangeRate() {
      this.dlog("exchangeRate button clicked");
      this.$emit("exchangeRate");
    },
    async processPayment() {
      // Prevent duplicate submissions if already processing
      if (this.isProcessing || this.isLoading) {
        return;
      }

      // Ensure currency is set before processing payment
      if (!this.selectedCurrencyData) {
        this.setDefaultCurrency();

        if (!this.selectedCurrencyData) {
          this.paymentStatus = {
            type: "error",
            message: this.$t("currency_required"),
          };
          return;
        }
      }

      if (!this.validatePayment()) {
        return;
      }

      this.isLoading = true;
      try {
        const paymentDetails = this.getPaymentDetails();

        this.dlog("submit", {
          method: this.selectedTab,
          details: paymentDetails,
          currency: this.selectedCurrencyData,
        });

        // Mark payment as completed to preserve payment method during reset
        this.isPaymentCompleted = true;

        this.$emit("submit", paymentDetails);
      } catch (error) {
        this.paymentStatus = {
          type: "error",
          message: "payment_error",
        };
        toast(this, "payment_error", "error");
      } finally {
        this.isLoading = false;
      }
    },
    // currency
    setDefaultCurrency() {
      // First, determine if the cart has a specific currency (e.g., THB, USD)
      const targetCurrencyCode =
        this.store.currency?.code ||
        this.currentAmountInfo?.currency ||
        null;

      if (targetCurrencyCode && this.activeCurrencies && this.activeCurrencies.length > 0) {
        const matchedCurrency = this.activeCurrencies.find(
          (c) => c.code === targetCurrencyCode || (this.store.currency?.id && c.id === this.store.currency.id)
        );
        if (matchedCurrency) {
          this.selectedCurrency = matchedCurrency.id;
          this.selectedCurrencyData = matchedCurrency;
          this.convertTotalAmount();
          return;
        }
      }

      if (this.store.currency) {
        this.selectedCurrency = this.store.currency.id || null;
        this.selectedCurrencyData = this.store.currency;
        this.convertTotalAmount();
        return;
      }

      // If no currency set in store, default to LAK
      if (this.activeCurrencies && this.activeCurrencies.length > 0) {
        const lakCurrency = this.activeCurrencies.find((c) => c.code === "LAK");
        if (lakCurrency) {
          this.selectedCurrency = lakCurrency.id;
          this.selectedCurrencyData = lakCurrency;
          this.dlog("setDefaultCurrency ->", this.selectedCurrencyData);
          this.convertTotalAmount();
        } else {
          // Create fallback LAK currency
          const fallbackLAKCurrency = {
            id: 1,
            code: "LAK",
            name: "Lao Kip",
            symbol: "₭",
            store_exchange_rate: "1.00",
          };
          this.selectedCurrency = fallbackLAKCurrency.id;
          this.selectedCurrencyData = fallbackLAKCurrency;
          this.dlog(
            "setDefaultCurrency -> Using fallback LAK currency:",
            this.selectedCurrencyData
          );
          this.convertTotalAmount();
        }
      } else {
        // Create fallback LAK currency
        const fallbackLAKCurrency = {
          id: 1,
          code: "LAK",
          name: "Lao Kip",
          symbol: "₭",
          store_exchange_rate: "1.00",
        };
        this.selectedCurrency = fallbackLAKCurrency.id;
        this.selectedCurrencyData = fallbackLAKCurrency;
        this.dlog(
          "setDefaultCurrency -> Using fallback LAK currency:",
          this.selectedCurrencyData
        );
        this.convertTotalAmount();
      }
    },
    onCurrencyChange(currencyId) {
      this.selectedCurrencyData = this.activeCurrencies.find(
        (c) => c.id === currencyId
      );
      this.dlog("onCurrencyChange ->", this.selectedCurrencyData);
      this.convertTotalAmount();
      this.$emit("currency-change", this.selectedCurrencyData);
    },
    convertTotalAmount() {
      this.dlog("convertTotalAmount", {
        total: this.totalAmount,
        currency: this.selectedCurrencyData,
      });

      // Default to LAK if no currency is selected
      if (!this.selectedCurrencyData) {
        this.cashAmount = this.totalAmount.toString();
        this.transferAmount = this.totalAmount.toString();
        this.creditAmount = this.totalAmount.toString();
        return;
      }

      // If selected currency matches the cart currency (the currency of the amount), no conversion needed
      // This ensures that if cart is in THB, we display THB amount as-is without converting
      const cartCurrency = this.currentAmountInfo.currency;
      if (this.selectedCurrencyData.code === cartCurrency) {
        this.cashAmount = this.totalAmount.toString();
        this.transferAmount = this.totalAmount.toString();
        this.creditAmount = this.totalAmount.toString();
        return;
      }

      // Check if we need to convert from one currency to another
      if (this.selectedCurrencyData.store_exchange_rate) {
        // TEMPORARY FIX: Hardcode correct exchange rates for testing
        let targetExchangeRate = parseFloat(
          this.selectedCurrencyData.store_exchange_rate
        );
        if (
          this.selectedCurrencyData.code === "USD" &&
          targetExchangeRate === 1
        ) {
          targetExchangeRate = 22000; // Override incorrect USD rate
        }
        if (
          this.selectedCurrencyData.code === "THB" &&
          targetExchangeRate === 1
        ) {
          targetExchangeRate = 690; // Override incorrect THB rate
        }

        let finalAmount = this.totalAmount;

        // Step 1: Convert base amount to LAK if it's not already in LAK
        if (this.currentAmountInfo.currency !== "LAK") {
          const baseExchangeRate = parseFloat(
            this.currentAmountInfo.exchangeRate
          );
          if (baseExchangeRate > 0) {
            finalAmount = this.totalAmount * baseExchangeRate; // Convert to LAK
          }
        }

        // Step 2: Convert from LAK to target currency
        if (targetExchangeRate > 0) {
          finalAmount = finalAmount / targetExchangeRate;

          this.cashAmount = finalAmount.toString();
          this.transferAmount = finalAmount.toString();
          this.creditAmount = finalAmount.toString();
        }
      } else {
        this.cashAmount = this.totalAmount.toString();
        this.transferAmount = this.totalAmount.toString();
        this.creditAmount = this.totalAmount.toString();
      }
    },
    // QR methods
    onTabClick(value) {
      // Placeholder for potential analytics/debug
    },
    emitQRSubmit() {
      const paymentDetails = this.getPaymentDetails();
      this.$emit("submit", paymentDetails);
    },
    openManualPaymentDialog() {
      this.showManualPaymentDialog = true;
      this.manualPaymentReference = "";
      this.manualPaymentErrors = [];
    },
    async confirmManualPayment() {
      if (
        !this.manualPaymentReference ||
        this.manualPaymentReference.trim() === ""
      ) {
        this.manualPaymentErrors = [this.$t("payment_reference_required")];
        return;
      }
      this.manualPaymentErrors = [];
      this.manualPaymentLoading = true;
      try {
        // Delegate to parent to handle actual API; just emit success request
        this.$emit("manualPaymentSuccess", {
          ticket_id: this.qrCodeData?.ticket_id,
          reference: this.manualPaymentReference.trim(),
          amount: this.qrCodeData?.amount,
        });
        this.showManualPaymentDialog = false;
        this.manualPaymentReference = "";
      } catch (error) {
        toast(this, "manual_payment_failed", "error");
      } finally {
        this.manualPaymentLoading = false;
      }
    },
    switchToCashPayment() {
      this.$emit("switchToCash");
      this.selectedTab = "cash";
    },
    handleLaoQRError() {
      // silent fallback
    },
    // Public methods
    setQRData(qrData) {
      this.qrCodeData = qrData;
      this.qrLoading = false;
    },
    setLoading(loading) {
      this.qrLoading = loading;
    },
    // External currency setter to ensure immediate reaction when parent updates currency
    setExternalCurrency(currency) {
      try {
        this.dlog("setExternalCurrency ->", currency);
        if (!currency) {
          return;
        }

        this.selectedCurrencyData = currency;
        this.selectedCurrency = currency.id || null;

        this.convertTotalAmount();
      } catch (e) {
        // Error handling
      }
    },

    // Method to update amounts when external payment amount changes (from exchange rate)
    setExternalPaymentAmount(convertedAmount) {
      try {
        if (!convertedAmount || convertedAmount <= 0) {
          return;
        }

        // Update all payment method amounts with the converted amount
        const amountString = convertedAmount.toString();
        this.cashAmount = amountString;
        this.transferAmount = amountString;
        this.creditAmount = amountString;

        // Emit event to notify parent of amount change
        this.$emit("paymentAmountUpdated", {
          amount: convertedAmount,
          currency: this.selectedCurrencyData,
          formattedAmount: this.$options.filters.NumberFormat(amountString),
        });
      } catch (e) {
        // Error handling
      }
    },
    reset(preservePaymentMethod = false) {
      this.cashAmount = "0";
      this.transferAmount = "0";
      this.creditAmount = "0";

      // Only reset to cash if we're not preserving the payment method
      if (!preservePaymentMethod) {
        this.selectedTab = "cash";
      }

      this.activeInput = "cash";
      this.paymentStatus = null;
      this.isLoading = false;
      this.selectedCurrency = null;
      this.selectedCurrencyData = null;
      this.isPaymentCompleted = false;

      // Re-initialize currency after reset
      this.setDefaultCurrency();
    },
    resetQR() {
      this.qrCodeData = null;
      this.qrLoading = false;
      this.manualPaymentReference = "";
      this.manualPaymentErrors = [];
      this.showManualPaymentDialog = false;
    },
  },
  watch: {
    totalAmount: {
      handler(newAmount, oldAmount) {
        const currentAmount = parseFloat(this.getPaymentAmount()) || 0;

        // Update amounts if:
        // 1. All amounts are 0 (indicating reset state)
        // 2. Current amount is close to the new total (within 0.01) - user typing
        // 3. Total changed significantly from previous prop value (new payment scenario)
        // 4. Current displayed amount differs significantly from new total (prop changed while component was open)
        const isResetState =
          this.cashAmount === "0" &&
          this.transferAmount === "0" &&
          this.creditAmount === "0";
        const isCloseToNewAmount = Math.abs(currentAmount - newAmount) < 0.01;
        // If total prop changed significantly (new payment scenario), always update
        const isSignificantPropChange =
          oldAmount !== undefined && Math.abs(oldAmount - newAmount) > 0.01;
        // If current displayed amount differs significantly from new total, update (prop changed)
        const isSignificantDisplayChange =
          !isCloseToNewAmount && Math.abs(currentAmount - newAmount) > 0.01;

        if (
          isResetState ||
          isCloseToNewAmount ||
          isSignificantPropChange ||
          isSignificantDisplayChange
        ) {
          if (this.selectedTab === "cash")
            this.cashAmount = newAmount.toString();
          else if (this.selectedTab === "transfer")
            this.transferAmount = newAmount.toString();
          else if (this.selectedTab === "credit")
            this.creditAmount = newAmount.toString();
          else if (this.selectedTab === "qr")
            this.cashAmount = newAmount.toString();
        }
      },
      immediate: true,
    },
    selectedTab: {
      handler(newTab, oldTab) {
        // removed verbose selectedTab change debug log

        this.paymentStatus = null;

        if (newTab === "transfer") {
          this.transferAmount = this.totalAmount.toString();
          this.activeInput = "transfer";
        } else if (newTab === "credit") {
          this.creditAmount = this.totalAmount.toString();
          this.activeInput = "credit";
        } else if (newTab === "cash") {
          this.cashAmount = this.totalAmount.toString();
          this.activeInput = "cash";
        } else if (newTab === "cod") {
          // Prefill COD with the same amount behavior as cash
          this.cashAmount = this.totalAmount.toString();
          this.activeInput = "cash";
        } else if (newTab === "cash_transfer") {
          this.cashAmount = "0";
          this.transferAmount = "0";
          this.activeInput = "cash";
          this.validatePayment();
        } else if (newTab === "qr") {
          if (this.isQRActive) {
            this.qrLoading = true;
            setTimeout(() => {
              this.emitQRSubmit();
            }, 100);
          } else {
            this.qrLoading = false;
            this.qrCodeData = null;
            // Use cash-like UX for amount entry but will submit as QR type
            this.cashAmount = this.totalAmount.toString();
            this.activeInput = "cash";
          }
        }
      },
      immediate: true,
    },
    activeCurrencies: {
      handler() {
        this.setDefaultCurrency();
      },
      immediate: true,
    },
    "store.currency"(newCurrency) {
      if (newCurrency) {
        this.selectedCurrencyData = newCurrency;
        this.selectedCurrency = newCurrency.id || null;
        this.convertTotalAmount();
        this.$emit("currency-change", newCurrency);
      }
    },
  },
};
</script>

<style scoped>
.payment-method-tabs {
  min-height: 500px;
  position: relative;
}

.payment-processing-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  z-index: 10;
  border-radius: 8px;
}
.payment-tab {
  text-transform: none;
  font-weight: 600;
  min-width: 100px;
}
.payment-buttons .payment-tab {
  background: transparent !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  height: 50px;
  line-height: 50px;
  color: inherit;
  border-bottom: 2px solid transparent;
  flex: 1 1 auto;
}
.payment-tab--active {
  color: #5b39c9 !important;
  border-bottom-color: #5b39c9 !important;
}
.payment-buttons {
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.payment-label {
  font-size: 13px;
}
.payment-content {
  background-color: transparent;
}
.payment-amount-display {
  text-align: center;
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
}
.payment-input-section {
  margin-top: 16px;
}
.active-input-indicator {
  display: flex;
  justify-content: center;
  gap: 8px;
}
.v-card.v-card--flat {
  border-radius: 8px;
}
.v-card.v-card--flat .v-card__text {
  padding: 12px;
}

.border-primary {
  border: 2px solid #5b39c9 !important;
  box-shadow: 0 0 0 1px #5b39c9 !important;
}

/* QR styles */
.qr-container {
  display: flex;
  justify-content: center;
  align-items: center;
}
.qr-wrapper {
  position: relative;
  width: 200px;
  height: 200px;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
}
.qr-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.qr-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}
.lao-qr-overlay {
  width: 25px;
  height: 25px;
  object-fit: contain;
  background-color: #fff;
  border-radius: 4px;
  padding: 4px;
}
.price-amount-section {
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
  margin-top: 16px;
}
.skeleton-qr-container {
  display: flex;
  justify-content: center;
  align-items: center;
}
.skeleton-price-section {
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
  margin-top: 16px;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .payment-tab {
    min-width: 80px;
    font-size: 12px;
  }
  .payment-label {
    font-size: 11px;
  }
}

/* Dark mode support */
.theme--dark .payment-amount-display {
  background-color: #424242;
}
.theme--dark .v-card.v-card--flat {
  background-color: #333;
}
</style>
